import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import { zoomDragStyle } from "../lib/pointerDrop";
import { createPortal } from "react-dom";
import { AutoFitText as AutoFitName } from "./AutoFitText";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Monitor, Tablet, Smartphone } from "lucide-react";
import { api } from "../lib/api";
import { formatHeight, getHeightUnit } from "../lib/units";
import { SectionHeading } from "./Loader";
import { PlayerAvatar } from "./PlayerAvatar";

const FW_LABELS = ["LW", "C", "RW"];
const D_LABELS = ["LD", "RD"];
export const G_LABELS = ["Starter", "Backup"];
const TONE = { f: "#E9D5FF", d: "#93C5FD", g: "#86EFAC" };
const EMPTY = { player_id: "", player_name: "" };
const FALLBACK = "#9D4CDD";
const TAG_PURPLE = "#9D4CDD"; // "leaderboards buttons colour"

// Around-the-photo badge placement. Each of the three badges (jersey #, C/A,
// position) can be pinned around the avatar; "bc" = below the photo (in a row
// above the name, never covering the face), "off" = hidden.
export const DEFAULT_BADGES = { jersey: "tl", captain: "tr", position: "bc" };
export const BADGE_PLACEMENTS = [
    ["tl", "Top-left"], ["tr", "Top-right"], ["bl", "Bottom-left"], ["br", "Bottom-right"],
    ["tc", "Above (center)"], ["tcc", "On photo (top)"], ["bcc", "On photo (bottom)"],
    ["ml", "Left side"], ["mr", "Right side"], ["bc", "Below photo"], ["off", "Hidden"],
];
const CORNER_POS = {
    tl: { top: 0, left: 0, tx: "-40%", ty: "-40%" },
    tr: { top: 0, right: 0, tx: "40%", ty: "-40%" },
    bl: { bottom: 0, left: 0, tx: "-40%", ty: "40%" },
    br: { bottom: 0, right: 0, tx: "40%", ty: "40%" },
    tc: { top: 0, left: "50%", tx: "-50%", ty: "-115%" },
    tcc: { top: 0, left: "50%", tx: "-50%", ty: "-15%" },
    bcc: { bottom: 0, left: "50%", tx: "-50%", ty: "15%" },
    ml: { top: "50%", left: 0, tx: "-112%", ty: "-50%" },
    mr: { top: "50%", right: 0, tx: "112%", ty: "-50%" },
};

// Strength formations → slot count + forwards (for the split F/D layout).
export const STRENGTH = {
    "5v4": { n: 5, fwd: 3 }, "5v3": { n: 5, fwd: 3 }, "4v3": { n: 4, fwd: 2 },
    "4v5": { n: 4, fwd: 2 }, "3v5": { n: 3, fwd: 1 }, "3v4": { n: 3, fwd: 1 }, "3v3": { n: 3, fwd: 2 },
    "4v4": { n: 4, fwd: 2 },
};
export const PP_VIEWS = ["5v4", "5v3", "4v3"];
export const SH_VIEWS = ["4v5", "3v5", "3v4"];
export const EV_VIEWS = ["4v4", "3v3"];
const SPECIAL = ["pp", "sh", "ev"];
const prefixOf = (kind) => (kind === "pp" ? "pp" : kind === "sh" ? "pk" : kind === "ev" ? "ev" : "");
const shortFor = (box) => (box.kind === "pp" ? "PP" : box.kind === "sh" ? "PK" : box.kind === "ev" ? "EV" : (box.label || "U").slice(0, 2).toUpperCase());
const unitKeyFor = (box, view, line = 0) => { const base = box.kind === "unit" ? box.key : `${prefixOf(box.kind)}_${view}`; return line > 0 ? `${base}__${line + 1}` : base; };
const specialLines = (box, view) => Math.max(1, Math.min(4, (box.lines || {})[view] || 1));

const DEFAULT_BOXES = [
    { key: "lines", kind: "lines", label: "Lines", align: "center", w: "md" },
    { key: "pairs", kind: "pairs", label: "Pairs", align: "center", w: "sm" },
    { key: "goalies", kind: "goalies", label: "Goaltenders", align: "center", w: "sm" },
    { key: "pp", kind: "pp", label: "Power Play", align: "center", w: "md", dup: true, orient: "split", views: PP_VIEWS, view: "5v4", lines: { "5v4": 2 } },
    { key: "sh", kind: "sh", label: "Penalty Kill", align: "center", w: "sm", dup: true, orient: "split", views: SH_VIEWS, view: "4v5", lines: { "4v5": 2 } },
    { key: "ev", kind: "ev", label: "Even Strength", align: "center", w: "sm", dup: true, orient: "split", views: EV_VIEWS, view: "4v4", lines: {} },
    { key: "ea", kind: "extra", label: "Extra Player", align: "center", w: "sm" },
    { key: "shootout", kind: "shootout", label: "Shootout", align: "center", w: "sm" },
    { key: "reserves", kind: "reserves", label: "Reserves", align: "center", w: "sm" },
];

// ----- icon / text sizing (admin sets in px) -----
const DEF_ICON = 44, DEF_TEXT = 11;
export const buildSz = (iconPx, textPx) => {
    const ic = Math.max(20, Math.min(96, iconPx || DEF_ICON));
    const tx = Math.max(7, Math.min(22, textPx || DEF_TEXT));
    return { iconPx: ic, textPx: tx, tagPx: Math.max(7, Math.round(tx * 0.78)), nameMax: Math.round(ic * 2.1), rowMin: ic + tx + 30 };
};

const WIDTHS = ["sm", "md", "lg", "xl", "full"];
const ALIGN_TEXT = { left: "text-left", center: "text-center", right: "text-right" };
const ALIGN_ITEMS = { left: "justify-items-start", center: "justify-items-center", right: "justify-items-end" };
const ALIGN_FLEX = { left: "justify-start", center: "justify-center", right: "justify-end" };
const abbrName = (name) => { const p = (name || "").trim().split(/\s+/); return p.length < 2 ? name : `${p[0][0]}. ${p.slice(1).join(" ")}`; };
const disp = (name, lay) => (lay?.abbreviate ? abbrName(name) : name);
const lineLabel = (lay, i) => lay.labels[`line${i}`] || (i === 4 ? "Extra Forwards" : `Line ${i + 1}`);
const pairLabel = (lay, i) => lay.labels[`pair${i}`] || (i === 3 ? "Extra Defenders" : `Pair ${i + 1}`);
const labelOf = (lay, key, fallback) => lay.labels[key] || fallback;
const accentOf = (box, lineup, primary) => (lineup?.accents || {})[box.key] || primary || FALLBACK;
const getUnitSlots = (lu, key, n) => { const a = (lu?.special_units || {})[key] || []; return Array.from({ length: n }, (_, i) => a[i] || EMPTY); };
const posOf = (slot, def) => (slot && (slot.pos || slot.position)) || def || "";
// A player's OWN natural position (LD/RW/C/…) — used to auto-label special-team
// slots so e.g. an LD dropped into a RW slot still shows "LD" without any typing.
const autoPos = (meta) => String((meta?.pref_position || meta?.position || "")).toUpperCase();

const ageFromBirthdate = (bd) => {
    if (!bd) return "—";
    const d = new Date(bd);
    if (isNaN(d.getTime())) return "—";
    const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
    return a >= 0 && a < 120 ? String(a) : "—";
};

// Fits a name on ONE line — shared AutoFitText imported above (aliased AutoFitName).

// ---- layout: shared box DEFINITIONS + per-(view × device) presentation VARIANTS ----
const DEVICES = ["pc", "tablet", "mobile"];
const DEVICE_ICON = { pc: Monitor, tablet: Tablet, mobile: Smartphone };
const comboKey = (view, device) => `${view}|${device}`;
const detectDevice = (w) => (w < 768 ? "mobile" : w < 1024 ? "tablet" : "pc");

function useNarrow(bp = 700) {
    const [n, setN] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
    useEffect(() => {
        const on = () => setN(window.innerWidth < bp);
        on();
        window.addEventListener("resize", on);
        return () => window.removeEventListener("resize", on);
    }, [bp]);
    return n;
}
const CANVAS_MAX = { pc: "100%", tablet: "900px", mobile: "440px" };

const normalizeDef = (b) => {
    const kind = b.kind || "unit";
    const isSpecial = SPECIAL.includes(kind);
    const n = kind === "unit" ? Math.max(1, Math.min(9, b.n || 5)) : undefined;
    let views = kind === "pp" ? (b.views || PP_VIEWS) : kind === "sh" ? (b.views || SH_VIEWS) : kind === "ev" ? (b.views || EV_VIEWS) : undefined;
    if (kind === "sh" && views) views = views.filter((v) => v !== "3v3");
    if (views && views.length === 0) views = kind === "sh" ? SH_VIEWS : kind === "pp" ? PP_VIEWS : EV_VIEWS;
    return {
        key: b.key, kind,
        label: b.label ?? "",
        align: ["left", "center", "right"].includes(b.align) ? b.align : "center",
        w: WIDTHS.includes(b.w) ? b.w : "md",
        dup: isSpecial ? b.dup !== false : false,
        orient: isSpecial ? (b.orient === "row" ? "row" : "split") : undefined,
        n, fwd: kind === "unit" ? (b.fwd ?? Math.max(1, n - 2)) : undefined,
        views, view: views ? (views.includes(b.view) ? b.view : views[0]) : undefined,
        lines: isSpecial ? (b.lines && typeof b.lines === "object" ? b.lines : {}) : undefined,
    };
};

// ---- presence helpers (hide empty sections on the public view) ----
const extraSlots = (lu) => ((lu?.special_units || {}).ea || []).filter((s) => s?.player_name);
const irSlots = (lu) => ((lu?.special_units || {}).ir || []).filter((s) => s?.player_name);
const ltirSlots = (lu) => ((lu?.special_units || {}).ltir || []).filter((s) => s?.player_name);
const reserveSlots = (lu) => (lu?.reserves || []).filter((s) => s?.player_name);
const shooterSlots = (lu) => (lu?.shootout_order || []).filter((s) => s?.player_name);
const unitViewHasPlayers = (lu, box, view) => {
    const cnt = specialLines(box, view); const n = STRENGTH[view].n;
    for (let li = 0; li < cnt; li++) { const uk = unitKeyFor(box, view, li); if (getUnitSlots(lu, uk, n).some((s) => s?.player_name)) return true; }
    return false;
};
const playableViews = (lu, box) => (box.views || []).filter((v) => unitViewHasPlayers(lu, box, v));
const specialBoxHasPlayers = (lu, box) => playableViews(lu, box).length > 0;
const reservesGroupHasPlayers = (lu) => reserveSlots(lu).length > 0 || irSlots(lu).length > 0 || ltirSlots(lu).length > 0;

const layoutOf = (raw) => {
    const l = raw && typeof raw === "object" ? raw : {};
    let defs = (Array.isArray(l.boxes) && l.boxes.length ? l.boxes : DEFAULT_BOXES).map(normalizeDef);
    DEFAULT_BOXES.forEach((db) => { if (!defs.some((b) => b.key === db.key || b.kind === db.kind)) defs.push(normalizeDef(db)); });
    const publicView = l.publicView === "simple" ? "simple" : "full";
    const labels = { ...(l.labels || {}) };
    const accents = { ...(l.accents || {}) };
    let variants = l.variants && typeof l.variants === "object" ? { ...l.variants } : null;
    if (!variants) variants = { [comboKey("special", "pc")]: baseVariantRaw(defs) };
    return { publicView, boxes: defs, labels, accents, variants };
};
const baseVariantRaw = (defs) => ({ iconPx: DEF_ICON, textPx: DEF_TEXT, tagStyle: "accent", abbreviate: false, ord: defs.map((b) => b.key), ov: {} });
const baseVariant = (lay) => baseVariantRaw(lay.boxes);

const getVariant = (lay, view, device) => {
    const key = comboKey(view, device);
    const stored = lay.variants && lay.variants[key];
    const base = baseVariant(lay);
    if (stored) return { ...base, ...stored, ov: stored.ov || {}, ord: stored.ord || base.ord };
    const src = (lay.variants && (lay.variants[comboKey("special", "pc")] || lay.variants[comboKey("full", "pc")])) || base;
    const v = JSON.parse(JSON.stringify({ ...base, ...src }));
    v.ord = v.ord || base.ord; v.ov = { ...(v.ov || {}) };
    return v;
};

const resolveBoxes = (lay, variant) => {
    const byKey = {}; lay.boxes.forEach((b) => { byKey[b.key] = b; });
    let ord = (variant.ord || []).filter((k) => byKey[k]);
    lay.boxes.forEach((b) => { if (!ord.includes(b.key)) ord.push(b.key); });
    return ord.map((k) => ({ ...byKey[k], ...((variant.ov || {})[k] || {}) }));
};

const REGULAR_VIEW_KINDS = ["lines", "pairs", "goalies", "reserves"];

const effLay = (lay, variant) => ({ abbreviate: variant.abbreviate, labels: lay.labels });

function usePublicDevice() {
    const [d, setD] = useState(() => detectDevice(typeof window === "undefined" ? 1280 : window.innerWidth));
    useEffect(() => { const on = () => setD(detectDevice(window.innerWidth)); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, []);
    return d;
}

const unitSpecs = (boxes) => {
    const specs = [];
    boxes.forEach((b) => {
        if (SPECIAL.includes(b.kind)) (b.views || []).forEach((v) => { const c = specialLines(b, v); for (let i = 0; i < c; i++) specs.push({ unitKey: unitKeyFor(b, v, i), n: STRENGTH[v].n, dup: b.dup }); });
        else if (b.kind === "unit") specs.push({ unitKey: b.key, n: b.n, dup: b.dup });
    });
    return specs;
};
const boxForUnitKey = (boxes, unitKey) => boxes.find((b) => (b.kind === "pp" && unitKey.startsWith("pp_")) || (b.kind === "sh" && unitKey.startsWith("pk_")) || (b.kind === "ev" && unitKey.startsWith("ev_")) || (b.kind === "unit" && b.key === unitKey));

// ============================================================================
//  COLUMN MODEL — shared by public + admin
//  column = { key, header?:{title,titleKey,accent,views?,view?,viewKey}, sections:[section] }
//  section = { key, title, titleKey, accent, body }
//  body = {t:"rows", rows:[{label?,labelKey?,cols,slots}]} | {t:"unit", rows:[{cols,slots}]} | {t:"list", field, items, numbered}
//  slot = { value, tone, posDefault, dndId?, clear?, posSetter? }
// ============================================================================
const buildColumns = (source, mode, lay, viewSel, editable, mut) => {
    const lu = source;
    const boxesByKind = {}; lay.boxes.forEach((b) => { boxesByKind[b.kind] = b; });
    const accents = lay.accents || {};
    const accOf = (key) => accents[key] || mut?.primary || FALLBACK;
    const title = (key, fb) => labelOf(lay, key, fb);

    const fwSlots = (line, i) => line.map((s, j) => ({ value: s, tone: "f", posDefault: FW_LABELS[j], dndId: `slot:fw-${i}-${j}`, clear: () => mut?.clearFixed(`fw-${i}-${j}`), posSetter: (v) => mut?.setFixedPos(`fw-${i}-${j}`, v) }));
    const dpSlots = (pair, i) => pair.map((s, j) => ({ value: s, tone: "d", posDefault: D_LABELS[j], dndId: `slot:dp-${i}-${j}`, clear: () => mut?.clearFixed(`dp-${i}-${j}`), posSetter: (v) => mut?.setFixedPos(`dp-${i}-${j}`, v) }));

    const linesSec = { key: "lines", title: title("lines", "Lines"), titleKey: "lines", accent: accOf("lines"),
        body: { t: "rows", rows: (lu.forward_lines || []).map((l, i) => ({ label: lineLabel(lay, i), labelKey: `line${i}`, cols: 3, slots: fwSlots(l, i) })) } };
    const pairsSec = { key: "pairs", title: title("pairs", "Pairs"), titleKey: "pairs", accent: accOf("pairs"),
        body: { t: "rows", rows: (lu.d_pairs || []).map((p, i) => ({ label: pairLabel(lay, i), labelKey: `pair${i}`, cols: 2, slots: dpSlots(p, i) })) } };
    const goaliesSec = { key: "goalies", title: title("goalies", "Goaltenders"), titleKey: "goalies", accent: accOf("goalies"),
        body: { t: "rows", rows: [{ cols: 2, slots: (lu.goalies || []).map((s, j) => ({ value: s, tone: "g", posDefault: G_LABELS[j], dndId: `slot:g-${j}`, clear: () => mut?.clearFixed(`g-${j}`), posSetter: (v) => mut?.setFixedPos(`g-${j}`, v) })) }] } };

    const unitSec = (box, v, li) => {
        const n = STRENGTH[v].n, fwd = STRENGTH[v].fwd, cnt = specialLines(box, v);
        const uk = unitKeyFor(box, v, li); const slots = getUnitSlots(lu, uk, n);
        const skey = box.kind === "ev" ? `ev_${v}` : `${prefixOf(box.kind)}${li + 1}`;
        const defTitle = box.kind === "ev" ? v : `${shortFor(box)}${cnt > 1 ? ` ${li + 1}` : ""}`;
        const mk = (s, j, tone) => ({ value: s, tone, posDefault: "", dndId: `su:${uk}:${j}`, clear: () => mut?.clearUnit(uk, j), posSetter: (val) => mut?.setUnitPos(uk, j, val) });
        const rows = [{ cols: fwd, slots: slots.slice(0, fwd).map((s, j) => mk(s, j, "f")) }];
        if (n > fwd) rows.push({ cols: n - fwd, slots: slots.slice(fwd).map((s, j) => mk(s, fwd + j, "d")) });
        return { key: skey, title: title(skey, defTitle), titleKey: skey, accent: accOf(skey), body: { t: "unit", rows } };
    };
    const rawList = (field) => (field === "shootout" ? (lu.shootout_order || []) : field === "reserves" ? (lu.reserves || []) : ((lu.special_units || {})[field] || []));
    const listFor = (field) => rawList(field).filter((s) => s?.player_name);
    const listSec = (field, fb) => ({ key: field, title: title(field, fb), titleKey: field, accent: accOf(field),
        body: { t: "list", field, numbered: field === "shootout",
            items: (editable ? rawList(field) : listFor(field)).map((s, idx) => ({ value: s, idx, tone: field === "g3" ? "g" : undefined, clear: () => mut?.clearList(field, idx), posSetter: (v) => mut?.setListPos(field, idx, v) })) } });

    const cols = [
        { key: "lines", sections: [linesSec] },
        { key: "pairs", sections: [pairsSec, goaliesSec] },
    ];
    const pp = boxesByKind.pp, pk = boxesByKind.sh, ev = boxesByKind.ev;
    if (pp) { const v = viewSel.pp || pp.view || pp.views[0]; const cnt = specialLines(pp, v); const secs = []; for (let i = 0; i < cnt; i++) secs.push(unitSec(pp, v, i)); cols.push({ key: "pp", header: { title: title("col_pp", "Power Play"), titleKey: "col_pp", accent: accOf("col_pp"), views: pp.views, view: v, viewKey: "pp" }, sections: secs }); }
    if (pk) { const v = viewSel.sh || pk.view || pk.views[0]; const cnt = specialLines(pk, v); const secs = []; for (let i = 0; i < cnt; i++) secs.push(unitSec(pk, v, i)); cols.push({ key: "sh", header: { title: title("col_sh", "Penalty Kill"), titleKey: "col_sh", accent: accOf("col_sh"), views: pk.views, view: v, viewKey: "sh" }, sections: secs }); }
    if (ev) { cols.push({ key: "ev", header: { title: title("col_ev", "Even Strength"), titleKey: "col_ev", accent: accOf("col_ev") }, sections: ev.views.map((v) => unitSec(ev, v, 0)) }); }
    cols.push({ key: "ea", sections: [listSec("ea", "Extra Player")] });
    cols.push({ key: "shootout", sections: [listSec("shootout", "Shootout")] });
    cols.push({ key: "reserves", sections: [listSec("reserves", "Scratch"), listSec("ir", "IR"), listSec("ltir", "LTIR"), listSec("g3", "3rd Goalie")] });
    return cols;
};

// ============================================================================
//  RENDER PRIMITIVES
// ============================================================================
const POS_COLOR = (t) => { const s = (t || "").toUpperCase(); if (!s) return "#E9D5FF"; if (s === "G" || s[0] === "G") return "#86EFAC"; if (/D$/.test(s)) return "#93C5FD"; return "#E9D5FF"; };
const PosTag = ({ text, tone, tagStyle, sz, editable, onChange, gold, corner }) => {
    if (!text && !editable && !gold) return null;
    const color = gold ? "#F6C453" : tagStyle === "white" ? "#FFFFFF" : (tone ? TONE[tone] : POS_COLOR(text));
    if (corner) {
        return <span className="rounded-md border border-white/15 bg-[#0A0B10] font-bold uppercase tracking-wider leading-none text-center inline-flex items-center justify-center"
            style={{ fontSize: sz.tagPx, color, padding: "2px 4px", boxShadow: "0 1px 3px rgba(0,0,0,.6)" }}>{text}</span>;
    }
    const base = "absolute left-1/2 -translate-x-1/2 rounded-md border border-white/15 bg-[#0A0B10] font-bold uppercase tracking-wider leading-none text-center";
    const style = { fontSize: sz.tagPx, color, bottom: -Math.round(sz.tagPx * 0.65) };
    if (editable) return (
        <input value={text} onChange={(e) => onChange(e.target.value.toUpperCase().slice(0, 6))} onMouseDown={(e) => e.stopPropagation()}
            className={`${base} focus:outline-none focus:ring-1 focus:ring-white/40`}
            style={{ ...style, width: Math.max(24, sz.tagPx * 3.4), padding: "2px 2px" }} placeholder="POS" data-testid="slot-pos-input" />
    );
    return <span className={base} style={{ ...style, padding: "2px 5px" }}>{text}</span>;
};

// A single small chip used for all three player badges (jersey #, C/A, position).
// Style is admin-chosen: "team" (team colour), "bw" (black & white), or
// "position" (tinted by the player's position colour).
const chipColors = (style, accent, posColorHex) => {
    if (style === "bw") return { bg: "#0A0B10", fg: "#FFFFFF", bd: "rgba(255,255,255,.4)" };
    if (style === "position") return { bg: posColorHex || "#E9D5FF", fg: "#0A0B10", bd: "rgba(0,0,0,.25)" };
    return { bg: accent || "#111726", fg: "#FFFFFF", bd: "rgba(255,255,255,.22)" }; // team
};
const Badge = ({ text, style, accent, posColorHex, sz, gold, gray, size = "md" }) => {
    const c = gold ? { bg: "#F6C453", fg: "#0A0B10", bd: "#F6C453" }
        : gray ? { bg: "#6B7280", fg: "#FFFFFF", bd: "#9CA3AF" }
            : chipColors(style, accent, posColorHex);
    const m = size === "sm" ? 0.82 : size === "lg" ? 1.28 : 1;
    return (
        <span className="rounded-[4px] font-black uppercase leading-none inline-flex items-center justify-center border" data-testid="lineup-badge"
            style={{ background: c.bg, color: c.fg, borderColor: c.bd, fontSize: Math.round(sz.tagPx * 0.98 * m), minWidth: Math.round(sz.tagPx * 1.45 * m), padding: "1px 3px", boxShadow: "0 1px 3px rgba(0,0,0,.55)" }}>{text}</span>
    );
};
const CornerWrap = ({ corner, dx = 0, children }) => {
    const c = CORNER_POS[corner];
    if (!c) return null;
    return <span className="absolute z-10" style={{ top: c.top, bottom: c.bottom, left: c.left, right: c.right, transform: `translate(calc(${c.tx} + ${dx}px), ${c.ty})` }}>{children}</span>;
};
const hexA = (hex, pct) => {
    if (!hex || hex[0] !== "#") return hex;
    let h = hex.slice(1); if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const n = parseInt(h, 16); const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return `rgba(${r},${g},${b},${Math.max(0, Math.min(100, pct)) / 100})`;
};

// Generic instant cursor-following portal tooltip box. This is the single
// source of truth for the "nicely designed box that appears instantly on
// hover" — shared by the lineup player HoverInfo AND the Game Centre stat
// column headers so both surfaces are visually + functionally identical.
export const HoverCard = ({ title, rows = [], children, style, className = "", testid = "hover-card-tip" }) => {
    const [tip, setTip] = useState(null);
    return (
        <span className={`relative inline-flex ${className}`} style={style}
            onMouseEnter={(e) => setTip({ x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => setTip((t) => (t ? { x: e.clientX, y: e.clientY } : t))}
            onMouseLeave={() => setTip(null)}>
            {children}
            {tip && createPortal(
                (() => {
                    const flipLeft = tip.x > window.innerWidth - 240;
                    return (
                <div className="fixed z-[9999] pointer-events-none rounded-lg border border-white/15 bg-[#0A0B10]/95 shadow-xl shadow-black/50 px-3 py-2" data-testid={testid}
                    style={{ left: flipLeft ? undefined : tip.x + 16, right: flipLeft ? (window.innerWidth - tip.x + 16) : undefined, top: Math.max(8, tip.y - 14) }}>
                    <p className="text-[11px] font-black uppercase tracking-wider text-white mb-1.5">{title}</p>
                    <div className="space-y-0.5">
                        {rows.map((r) => (
                            <p key={r.k} className="flex items-center justify-between gap-6 text-[10px] leading-tight whitespace-nowrap">
                                <span className="text-white/40 uppercase tracking-wider">{r.k}</span>
                                <span className="font-bold" style={{ color: r.c || "#E2E8F0" }}>{r.v}</span>
                            </p>
                        ))}
                    </div>
                </div>
                    );
                })(), document.body)}
        </span>
    );
};

// Instant hover-info popover (cursor-following). Shared by the lineup Slot and
// the Game Centre player tables so both surfaces show identical player info.
export const HoverInfo = ({ meta = {}, name, tone, children, style, className = "" }) => {
    const prefP = String(meta.pref_position || meta.position || "").toUpperCase();
    const secP = String(meta.secondary_position || "").toUpperCase();
    const shootsFull = ({ L: "Left", R: "Right" })[String(meta.shoots || "").toUpperCase()] || meta.shoots || "—";
    const tipRows = [
        { k: "Preferred Position", v: prefP || "—", c: prefP ? POS_COLOR(prefP) : undefined },
        { k: "Secondary Position", v: (secP && secP !== prefP) ? secP : "—", c: (secP && secP !== prefP) ? POS_COLOR(secP) : undefined },
        { k: tone === "g" ? "Catches" : "Shoots", v: shootsFull },
        { k: "Height", v: meta.height_cm ? formatHeight(meta.height_cm, getHeightUnit()) : "—" },
        { k: "Age", v: ageFromBirthdate(meta.birthdate) },
        { k: "Nationality", v: meta.nationality || "—" },
    ];
    if (meta.foa != null && Number(meta.foa) > 0) {
        tipRows.push({ k: "Faceoffs (W/A)", v: `${meta.fow ?? 0} / ${meta.foa}` });
        tipRows.push({ k: "Faceoff %", v: `${meta.fo_pct ?? 0}%` });
    }
    return <HoverCard title={name} rows={tipRows} style={style} className={className} testid="lineup-slot-tip">{children}</HoverCard>;
};

export const Slot = ({ slot, posDefault, tone, playerMap, lay, sz, tagStyle, noLink = false, accent, badges, badgeStyle = "team", boxStyle = "dark", boxShape = "rounded", boxSize = "md", boxOpacity = 85, jerseySize = "md", positionSize = "md", jerseyHash = false, liveIdentity = false, prefFirst = false, nudges, cardW: cardWOverride, cardH: cardHOverride }) => {
    const name = slot?.player_name || "";
    const id = slot?.player_id || "";
    const bd = badges || DEFAULT_BADGES;
    const hasBelow = Object.values(bd).includes("bc");
    const padScale = boxSize === "sm" ? 0.22 : boxSize === "lg" ? 0.66 : 0.42;
    const pad = Math.round(sz.tagPx * (0.7 + padScale * 1.6));
    const cardW = (cardWOverride && cardWOverride > 0) ? cardWOverride : sz.nameMax + pad * 2;
    const cardH = (cardHOverride && cardHOverride > 0) ? cardHOverride : pad * 2 + sz.iconPx + Math.round(sz.textPx * 1.35) + (hasBelow ? Math.round(sz.tagPx * 1.9) : 0);
    const baseFill = boxStyle === "black" ? "#000000" : boxStyle === "team" ? (accent || "#1B2130") : boxStyle === "none" ? "transparent" : "#1B2130";
    const boxFill = boxStyle === "none" ? "transparent" : hexA(baseFill, boxOpacity);
    const boxRadius = boxShape === "circle" ? "9999px" : boxShape === "square" ? "4px" : `${Math.round(sz.iconPx * 0.22)}px`;
    const boxBorder = boxStyle === "none" ? "none" : `1px solid ${hexA("#FFFFFF", Math.min(14, boxOpacity / 6))}`;
    // Snug, uniform card (fixed width + min-height). Spacing between cards is
    // controlled by the section's flex gap, not the card width.
    const cardStyle = { width: cardW, minHeight: cardH, maxWidth: "100%" };
    if (!name) return <div aria-hidden="true" className="mx-auto" style={{ ...cardStyle, background: boxFill, borderRadius: boxRadius, border: boxBorder }} />;
    const meta = playerMap?.[id] || {};
    const VALID_POS = ["LW", "C", "RW", "LD", "RD", "D", "F", "G", "L", "R", "W"];
    const cleanPos = (p) => { const s = String(p || "").toUpperCase().trim(); return VALID_POS.includes(s) ? s : ""; };
    // A goalie is ALWAYS a goalie — never let a stale slot override (e.g. a reserve
    // stored as "F") mislabel a keeper. Otherwise use the slot/default/player pos.
    const isGoalie = String(meta?.pref_position || meta?.position || "").toUpperCase().startsWith("G");
    // Active lineup (liveIdentity) → the SLOT the player is placed in + their LIVE
    // jersey. Game centre → the PER-GAME roster position + jersey (from that game's
    // data, surfaced via the player meta), never the player's current values.
    const tag = isGoalie ? "G" : prefFirst
        ? (cleanPos(meta?.pref_position) || cleanPos(meta?.position) || cleanPos(slot?.pos || slot?.position) || cleanPos(posDefault))
        : (liveIdentity
        ? (cleanPos(posDefault) || cleanPos(meta?.pref_position) || cleanPos(meta?.position) || cleanPos(slot?.pos || slot?.position))
        : (cleanPos(meta?.position) || cleanPos(slot?.pos || slot?.position) || cleanPos(posDefault) || cleanPos(meta?.pref_position)));
    const posColorHex = tone ? TONE[tone] : POS_COLOR(tag);
    const jerseyNum = String(meta.jersey ?? slot?.jersey ?? "").trim();
    const cap = String(slot?.captain || meta.captain || "").toUpperCase();
    const parts = {
        jersey: (jerseyNum && jerseyNum !== "0") ? <Badge text={(jerseyHash ? "#" : "") + jerseyNum} style={badgeStyle} accent={accent} posColorHex={posColorHex} sz={sz} size={jerseySize} /> : null,
        captain: (cap === "C" || cap === "A") ? <Badge text={cap} sz={sz} gold={cap === "C"} gray={cap === "A"} /> : null,
        position: tag ? <Badge text={tag} style={badgeStyle} accent={accent} posColorHex={posColorHex} sz={sz} size={positionSize} /> : null,
    };
    const corners = []; const below = [];
    ["jersey", "captain", "position"].forEach((k) => {
        const place = bd[k] || "off";
        if (place === "off" || !parts[k]) return;
        if (place === "bc") below.push(<span key={k}>{parts[k]}</span>);
        else corners.push(<CornerWrap key={k} corner={place} dx={nudges?.[k] || 0}>{parts[k]}</CornerWrap>);
    });
    const inner = (
        <div className="flex flex-col items-center justify-center mx-auto" data-testid="lineup-slot"
            style={{ ...cardStyle, background: boxFill, borderRadius: boxRadius, border: boxBorder, padding: pad }}>
            <HoverInfo meta={meta} name={name} tone={tone}>
                <span className="relative inline-flex" style={{ width: sz.iconPx, height: sz.iconPx }}>
                    <PlayerAvatar photo={meta.photo_url || ""} name={name} playerId={id} sizePx={sz.iconPx} photoX={meta.photo_x} photoY={meta.photo_y} photoZoom={meta.photo_zoom} />
                    {corners}
                </span>
            </HoverInfo>
            {below.length > 0 && <div className="flex items-center justify-center gap-1 flex-wrap" style={{ marginTop: Math.round(sz.tagPx * 0.55) }}>{below}</div>}
            <AutoFitName text={disp(name, lay)} maxWidth={sz.nameMax} baseSize={sz.textPx} className="font-bold text-white leading-tight text-center mx-auto mt-1" />
        </div>
    );
    return id && !noLink ? <Link to={`/players/${id}`} className="block hover:opacity-90 transition-opacity" data-testid={`lineup-slot-${id}`}>{inner}</Link> : inner;
};

const clone = (o) => JSON.parse(JSON.stringify(o));
const getEV = (d, key) => { const [t, a, b] = key.split("-"); if (t === "fw") return d.forward_lines[+a][+b]; if (t === "dp") return d.d_pairs[+a][+b]; return d.goalies[+a]; };
const writeEV = (d, key, val) => { const [t, a, b] = key.split("-"); if (t === "fw") d.forward_lines[+a][+b] = val; else if (t === "dp") d.d_pairs[+a][+b] = val; else d.goalies[+a] = val; };
const posTag = (p) => { const s = (p?.position || "").toUpperCase(); if (s.startsWith("G")) return "G"; if (s === "D" || /D$/.test(s)) return "D"; return "F"; };
const parseId = (id) => {
    if (id.startsWith("pool:")) return { kind: "pool", pid: id.slice(5) };
    if (id.startsWith("res:")) return { kind: "res", idx: +id.slice(4) };
    if (id.startsWith("so:")) return { kind: "so", idx: +id.slice(3) };
    if (id.startsWith("ea:")) return { kind: "ea", idx: +id.slice(3) };
    if (id.startsWith("ir:")) return { kind: "ir", idx: +id.slice(3) };
    if (id.startsWith("ltir:")) return { kind: "ltir", idx: +id.slice(5) };
    if (id.startsWith("g3:")) return { kind: "g3", idx: +id.slice(3) };
    if (id.startsWith("slot:")) return { kind: "ev", key: id.slice(5) };
    if (id.startsWith("su:")) { const [, u, i] = id.split(":"); return { kind: "su", unit: u, idx: +i }; }
    return {};
};
const ensureUnits = (d, boxes) => {
    d.special_units = d.special_units || {}; d.shootout_order = d.shootout_order || [];
    d.special_units.ea = d.special_units.ea || []; d.special_units.ir = d.special_units.ir || []; d.special_units.ltir = d.special_units.ltir || []; d.special_units.g3 = d.special_units.g3 || [];
    unitSpecs(boxes).forEach(({ unitKey, n }) => {
        const a = d.special_units[unitKey] || [];
        d.special_units[unitKey] = Array.from({ length: n }, (_, i) => (a[i] ? { player_id: a[i].player_id || "", player_name: a[i].player_name || "", pos: a[i].pos } : { ...EMPTY }));
    });
};
const removeFromExclusive = (d, pid, boxes) => {
    d.forward_lines.forEach((l) => l.forEach((s, j) => { if (s.player_id === pid) l[j] = { ...EMPTY }; }));
    d.d_pairs.forEach((l) => l.forEach((s, j) => { if (s.player_id === pid) l[j] = { ...EMPTY }; }));
    d.goalies.forEach((s, j) => { if (s.player_id === pid) d.goalies[j] = { ...EMPTY }; });
    d.reserves = (d.reserves || []).filter((r) => r.player_id !== pid);
    unitSpecs(boxes).filter((u) => !u.dup).forEach(({ unitKey }) => { if (d.special_units[unitKey]) d.special_units[unitKey] = d.special_units[unitKey].map((x) => (x && x.player_id === pid ? { ...EMPTY } : x)); });
};
const clearSource = (d, s) => {
    if (s.kind === "ev") writeEV(d, s.key, { ...EMPTY });
    else if (s.kind === "res") d.reserves = (d.reserves || []).filter((_, i) => i !== s.idx);
    else if (s.kind === "so") d.shootout_order = (d.shootout_order || []).filter((_, i) => i !== s.idx);
    else if (["ea", "ir", "ltir", "g3"].includes(s.kind)) d.special_units[s.kind] = ((d.special_units || {})[s.kind] || []).filter((_, i) => i !== s.idx);
    else if (s.kind === "su" && d.special_units[s.unit]) d.special_units[s.unit][s.idx] = { ...EMPTY };
};

// ---- editor chip ----
const EditChip = ({ name, photo, dragHandleProps, onRemove, dragging, meta, mini, lay, sz, posDefault, tone, slot, tagStyle, onPos, numbered, num }) => {
    const tag = posOf(slot, posDefault) || autoPos(meta);
    return (
        <div {...(dragHandleProps || {})} className={`relative flex flex-col items-center select-none cursor-grab active:cursor-grabbing ${dragging ? "opacity-90" : ""}`}>
            <span className="relative inline-flex" style={{ marginBottom: Math.round(sz.tagPx * 0.65) + 2 }}>
                <PlayerAvatar photo={photo} name={name} sizePx={mini ? Math.min(sz.iconPx, 40) : sz.iconPx} photoX={meta?.photo_x} photoY={meta?.photo_y} photoZoom={meta?.photo_zoom} />
                {numbered ? <PosTag text={String(num)} gold sz={sz} /> : onPos ? <PosTag text={tag} tone={tone} tagStyle={tagStyle} sz={sz} editable onChange={onPos} /> : <PosTag text={tag} tone={tone} tagStyle={tagStyle} sz={sz} />}
            </span>
            <div className="mx-auto" style={{ width: sz.nameMax, maxWidth: "100%" }}>
                <AutoFitName text={disp(name, lay)} maxWidth={sz.nameMax} baseSize={sz.textPx} className="font-bold text-white leading-tight text-center" />
            </div>
            {onRemove && <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={onRemove} className="absolute -top-1 -left-1 bg-[#0A0B10] border border-white/20 rounded-full p-0.5 text-[#FCA5A5] hover:text-[#EF4444] leading-none z-10" title="Remove">×</button>}
        </div>
    );
};

const EditSlot = ({ slotDesc, byId, lay, sz, tagStyle }) => {
    const did = slotDesc.dndId; const val = slotDesc.value; const pid = val?.player_id; const p = pid ? byId[pid] : null;
    return (
        <Droppable droppableId={did} type="PLAYER">
            {(provided, snap) => (
                <div ref={provided.innerRef} {...provided.droppableProps}
                    className={`relative rounded-md flex items-center justify-center p-1 border transition-colors ${snap.isDraggingOver ? "border-[#9D4CDD] bg-[#9D4CDD]/15" : pid ? "border-[#1E2233] bg-black/20" : "border-dashed border-[#2A2F45]"}`}
                    style={{ minHeight: sz.rowMin - 6 }}
                    data-testid={`lineup-edit-slot-${did.replace(/[:]/g, "-")}`}>
                    {pid ? (
                        <Draggable draggableId={did} index={0}>
                            {(prov, s) => <div ref={prov.innerRef} {...prov.draggableProps} style={zoomDragStyle(prov.draggableProps.style)}><EditChip dragHandleProps={prov.dragHandleProps} name={val.player_name || p?.name || pid} photo={p?.photo_url} meta={p} slot={val} posDefault={slotDesc.posDefault} tone={slotDesc.tone} tagStyle={tagStyle} onPos={slotDesc.posSetter} onRemove={slotDesc.clear} dragging={s.isDragging} lay={lay} sz={sz} /></div>}
                        </Draggable>
                    ) : (
                        <span className="rounded-full border border-dashed border-[#2A2F45] flex items-center justify-center text-[#3A3F55] font-bold uppercase tracking-wider" style={{ width: sz.iconPx, height: sz.iconPx, fontSize: sz.tagPx }}>{slotDesc.posDefault || "+"}</span>
                    )}
                    <div style={{ display: "none" }}>{provided.placeholder}</div>
                </div>
            )}
        </Droppable>
    );
};

// ---- shells ----
const HEADER_H = 28;
const EditableName = ({ value, onChange, className, style, testid }) => (
    <input value={value} onChange={(e) => onChange(e.target.value)} onMouseDown={(e) => e.stopPropagation()}
        className={`bg-transparent border-b border-white/10 focus:border-[#9D4CDD] focus:outline-none ${className}`} style={style} data-testid={testid} />
);

const RowCells = ({ cols, slots, editable, byId, playerMap, lay, sz, tagStyle }) => (
    <div className="grid gap-1 justify-items-center" style={{ gridTemplateColumns: `repeat(${Math.max(1, cols)}, minmax(0,1fr))` }}>
        {slots.map((sd, j) => editable
            ? <EditSlot key={j} slotDesc={sd} byId={byId} lay={lay} sz={sz} tagStyle={tagStyle} />
            : <Slot key={j} slot={sd.value} posDefault={sd.posDefault} tone={sd.tone} playerMap={playerMap} lay={lay} sz={sz} tagStyle={tagStyle} />)}
    </div>
);

const ListBody = ({ section, editable, byId, playerMap, lay, sz, tagStyle }) => {
    const filled = section.items.filter((it) => it.value?.player_name);
    if (editable) return (
        <Droppable droppableId={section.field} direction="horizontal" type="PLAYER">{(prov, snap) => (
            <div ref={prov.innerRef} {...prov.droppableProps} className={`rounded-md p-1 flex flex-wrap gap-2 content-start justify-center transition-colors ${snap.isDraggingOver ? "bg-[#9D4CDD]/10 ring-1 ring-[#9D4CDD]" : ""}`} style={{ minHeight: sz.iconPx + 22 }} data-testid={`lineup-edit-${section.field}`}>
                {section.items.length === 0 && !snap.isDraggingOver ? <p className="text-[10px] text-[#64748B] italic w-full text-center py-2">Drop here</p>
                    : section.items.map((it, k) => it.value?.player_name ? (
                        <Draggable key={`${section.field}-${it.idx}`} draggableId={`${section.field === "shootout" ? "so" : section.field === "reserves" ? "res" : section.field}:${it.idx}`} index={it.idx}>
                            {(p2) => <div ref={p2.innerRef} {...p2.draggableProps}>
                                <EditChip mini dragHandleProps={p2.dragHandleProps} name={it.value.player_name} photo={byId[it.value.player_id]?.photo_url} meta={byId[it.value.player_id]} slot={it.value} posDefault="" tone={it.tone} tagStyle={tagStyle} numbered={it.numbered} num={k + 1} onPos={it.numbered ? null : it.posSetter} onRemove={it.clear} lay={lay} sz={sz} />
                            </div>}
                        </Draggable>
                    ) : null)}
                {prov.placeholder}
            </div>
        )}</Droppable>
    );
    if (!filled.length) return <p className="text-[10px] text-[#64748B] italic text-center py-1.5">—</p>;
    return (
        <div className="flex flex-wrap gap-2 justify-center">
            {filled.map((it, k) => (
                <div key={k} className="flex flex-col items-center" style={{ width: sz.nameMax }}>
                    <span className="relative inline-flex" style={{ marginBottom: Math.round(sz.tagPx * 0.65) + 2 }}>
                        <Link to={it.value.player_id ? `/players/${it.value.player_id}` : "#"}>
                            <PlayerAvatar photo={playerMap?.[it.value.player_id]?.photo_url || ""} name={it.value.player_name} playerId={it.value.player_id} sizePx={sz.iconPx} />
                        </Link>
                        {it.numbered ? <PosTag text={String(k + 1)} gold sz={sz} /> : <PosTag text={posOf(it.value, "") || autoPos(playerMap?.[it.value.player_id])} tone={it.tone} tagStyle={tagStyle} sz={sz} />}
                    </span>
                    <AutoFitName text={disp(it.value.player_name, lay)} maxWidth={sz.nameMax} baseSize={sz.textPx} className="font-bold text-white leading-tight text-center mx-auto" />
                </div>
            ))}
        </div>
    );
};

const ViewTabBar = ({ header, onView }) => (
    <div className="flex items-center justify-center gap-1 flex-wrap" data-testid={`view-tabs-${header.viewKey}`}>
        {header.views.map((v) => <button key={v} type="button" onClick={() => onView(header.viewKey, v)} className={`px-1.5 py-0.5 rounded text-[9px] font-black tracking-wider transition-colors border border-white/20 ${header.view === v ? "bg-white text-black" : "bg-white/50 text-black/70 hover:bg-white hover:text-black"}`} data-testid={`view-tab-${header.viewKey}-${v}`}>{v}</button>)}
    </div>
);

const SectionBody = ({ sec, editable, byId, playerMap, lay, sz, tagStyle, onName }) => {
    const b = sec.body;
    if (b.t === "list") return <ListBody section={{ field: b.field, items: b.items, numbered: b.numbered }} editable={editable} byId={byId} playerMap={playerMap} lay={lay} sz={sz} tagStyle={tagStyle} />;
    return (
        <div className="flex flex-col gap-1">
            {b.rows.map((row, ri) => (
                <div key={ri} className="bg-white/[0.02] rounded-md px-1 pt-0.5 pb-1">
                    {b.t === "rows" && (row.labelKey || row.label) && (
                        <div className="text-center mb-0.5" style={{ minHeight: 12 }}>
                            {editable && row.labelKey
                                ? <EditableName value={row.label} onChange={(v) => onName(row.labelKey, v)} className="text-[8px] uppercase tracking-wider font-bold text-[#94A3B8] text-center w-full" testid={`row-name-${row.labelKey}`} />
                                : <p className="text-[8px] uppercase tracking-wider font-bold text-[#64748B]">{row.label}</p>}
                        </div>
                    )}
                    <RowCells cols={row.cols} slots={row.slots} editable={editable} byId={byId} playerMap={playerMap} lay={lay} sz={sz} tagStyle={tagStyle} />
                </div>
            ))}
        </div>
    );
};

const SectionBox = ({ sec, editable, onName, onAccent, ...rest }) => (
    <div className="rounded-xl bg-[#0E0F16] border border-white/10 overflow-hidden" style={{ borderTop: `3px solid ${sec.accent || FALLBACK}` }} data-testid={`lineup-sec-${sec.key}`}>
        <div className="px-1.5 py-1 flex items-center justify-center" style={{ minHeight: HEADER_H }}>
            {editable ? (
                <div className="w-full flex flex-col items-center gap-1">
                    <EditableName value={sec.title} onChange={(v) => onName(sec.titleKey, v)} className="text-[11px] uppercase tracking-wider font-black text-white/90 text-center w-full" testid={`sec-name-${sec.key}`} />
                    <div className="flex items-center justify-center gap-1">
                        <input type="color" value={sec.accent} onChange={(e) => onAccent(sec.titleKey, e.target.value)} className="h-5 w-6 rounded cursor-pointer bg-transparent border border-white/20 p-0" title="Colour wheel" data-testid={`sec-accent-${sec.key}`} />
                        <input value={sec.accent} onChange={(e) => { const v = e.target.value; if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onAccent(sec.titleKey, v); }} className="w-[68px] bg-[#0A0B10] border border-white/15 rounded px-1 py-0.5 text-[9px] text-white font-mono-stat uppercase text-center focus:outline-none focus:border-[#9D4CDD]" placeholder="#RRGGBB" data-testid={`sec-accent-hex-${sec.key}`} />
                    </div>
                </div>
            ) : <p className="text-[11px] uppercase tracking-wider font-black text-white/90 text-center truncate">{sec.title}</p>}
        </div>
        <div className="px-1.5 pb-1.5"><SectionBody sec={sec} editable={editable} onName={onName} {...rest} /></div>
    </div>
);

const ColumnBox = ({ col, editable, onName, onAccent, onView, onWidth, ...rest }) => (
    <div className="flex flex-col gap-2" data-testid={`lineup-col-${col.key}`}>
        {(col.header || editable) && (
            <div className="flex flex-col gap-0.5 px-1">
                <div className="flex items-center justify-center gap-1">
                    {editable && onWidth && <button type="button" onClick={() => onWidth(col.key, -0.25)} className="h-4 w-4 rounded bg-white/5 hover:bg-white/15 text-white/60 text-[12px] leading-none shrink-0" title="Narrower" data-testid={`col-width-minus-${col.key}`}>−</button>}
                    {col.header ? (
                        editable
                            ? <EditableName value={col.header.title} onChange={(v) => onName(col.header.titleKey, v)} className="text-[11px] uppercase tracking-wider font-black text-[#C9A6F0] text-center flex-1 min-w-0" testid={`col-name-${col.key}`} />
                            : <p className="text-[11px] uppercase tracking-wider font-black text-[#C9A6F0] text-center truncate">{col.header.title}</p>
                    ) : (editable && <span className="text-[9px] uppercase tracking-widest text-white/25 font-bold">{col.key}</span>)}
                    {editable && onWidth && <button type="button" onClick={() => onWidth(col.key, 0.25)} className="h-4 w-4 rounded bg-white/5 hover:bg-white/15 text-white/60 text-[12px] leading-none shrink-0" title="Wider" data-testid={`col-width-plus-${col.key}`}>+</button>}
                </div>
                {col.header && col.header.views && col.header.views.length > 1 && <ViewTabBar header={col.header} onView={onView} />}
            </div>
        )}
        {col.sections.map((sec) => <SectionBox key={sec.key} sec={sec} editable={editable} onName={onName} onAccent={onAccent} {...rest} />)}
    </div>
);

const LineupBoard = ({ columns, editable, byId, playerMap, lay, sz, tagStyle, colW, onName, onAccent, onView, onWidth }) => {
    const narrow = useNarrow(700);
    if (!columns.length) return null;
    const gridCols = narrow ? "minmax(0,1fr)" : columns.map((c) => `minmax(0, ${(colW && colW[c.key]) || 1}fr)`).join(" ");
    return (
        <div className="grid gap-2 items-start" style={{ gridTemplateColumns: gridCols }} data-testid="lineup-board">
            {columns.map((col) => (
                <ColumnBox key={col.key} col={col} editable={editable} byId={byId} playerMap={playerMap} lay={lay} sz={sz} tagStyle={tagStyle}
                    onName={onName} onAccent={onAccent} onView={onView} onWidth={editable ? onWidth : null} />
            ))}
        </div>
    );
};

function lockScroll() { document.documentElement.style.overflow = "hidden"; document.body.style.overflow = "hidden"; }
function unlockScroll() { document.documentElement.style.overflow = ""; document.body.style.overflow = ""; }

const SegBtn = ({ active, onClick, children, testid }) => (
    <button type="button" onClick={onClick} className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${active ? "bg-[#9D4CDD] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`} data-testid={testid}>{children}</button>
);

// ============================================================================
//  ADMIN TOOLBAR
// ============================================================================
const ToolbarControls = ({ variant, editDevice, setEditDevice, onVariant }) => {
    const numCls = "w-14 bg-[#0A0B10] border border-[#1E2233] rounded-md px-1.5 py-1 text-xs text-white focus:outline-none focus:border-[#9D4CDD]";
    return (
        <div className="rosterly-card p-2.5 space-y-2" data-testid="lineup-layout-controls">
            <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-[11px]">
                <div className="flex items-center gap-1.5" data-testid="layout-edit-device"><span className="text-white/60 font-bold uppercase tracking-wider">Device</span>
                    {DEVICES.map((d) => { const I = DEVICE_ICON[d]; return <button key={d} type="button" onClick={() => setEditDevice(d)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${editDevice === d ? "bg-[#9D4CDD] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`} data-testid={`layout-edit-device-${d}`}><I className="h-3 w-3" /> {d}</button>; })}
                </div>
                <label className="flex items-center gap-1.5 text-white/70" data-testid="size-icon"><span className="uppercase tracking-wider font-bold">Icon px</span>
                    <input type="number" min="20" max="96" value={variant.iconPx || DEF_ICON} onChange={(e) => onVariant({ iconPx: +e.target.value })} className={numCls} data-testid="size-icon-input" />
                </label>
                <label className="flex items-center gap-1.5 text-white/70" data-testid="size-text"><span className="uppercase tracking-wider font-bold">Text px</span>
                    <input type="number" min="7" max="22" value={variant.textPx || DEF_TEXT} onChange={(e) => onVariant({ textPx: +e.target.value })} className={numCls} data-testid="size-text-input" />
                </label>
                <div className="flex items-center gap-1.5" data-testid="tag-style"><span className="text-white/60 uppercase tracking-wider font-bold">Position tag</span>
                    <SegBtn active={(variant.tagStyle || "accent") === "accent"} onClick={() => onVariant({ tagStyle: "accent" })} testid="tag-style-accent">Colour</SegBtn>
                    <SegBtn active={variant.tagStyle === "white"} onClick={() => onVariant({ tagStyle: "white" })} testid="tag-style-white">White</SegBtn>
                </div>
                <label className="flex items-center gap-1.5 text-white/70 cursor-pointer ml-auto" data-testid="layout-abbreviate">
                    <input type="checkbox" checked={!!variant.abbreviate} onChange={(e) => onVariant({ abbreviate: e.target.checked })} className="accent-[#9D4CDD]" /> Abbreviate names
                </label>
            </div>
        </div>
    );
};

// ============================================================================
//  ADMIN EDITOR
// ============================================================================
function EditableLineup({ draft, setDraft, pool = [], players = [], primary, lay, commitLayout }) {
    const [q, setQ] = useState("");
    const [editDevice, setEditDevice] = useState("pc");
    const [viewSel, setViewSel] = useState({});

    const variant = useMemo(() => getVariant(lay, "special", "pc"), [lay]);
    const combo = comboKey("special", "pc");
    const defs = lay.boxes;
    const elay = useMemo(() => effLay(lay, variant), [lay, variant]);
    const sz = useMemo(() => buildSz(variant.iconPx, variant.textPx), [variant.iconPx, variant.textPx]);
    const tagStyle = variant.tagStyle || "accent";
    const byId = useMemo(() => { const m = {}; (players || []).forEach((p) => { m[p.id] = p; }); (pool || []).forEach((p) => { if (!m[p.id]) m[p.id] = p; }); return m; }, [players, pool]);

    const placed = new Set();
    (draft.forward_lines || []).forEach((l) => l.forEach((s) => s?.player_id && placed.add(s.player_id)));
    (draft.d_pairs || []).forEach((l) => l.forEach((s) => s?.player_id && placed.add(s.player_id)));
    (draft.goalies || []).forEach((s) => s?.player_id && placed.add(s.player_id));
    (draft.reserves || []).forEach((s) => s?.player_id && placed.add(s.player_id));
    const needle = q.trim().toLowerCase();
    const available = (pool || []).filter((p) => !placed.has(p.id)).filter((p) => !needle || (p.name || "").toLowerCase().includes(needle));

    const onVariant = (patch) => commitLayout({ ...lay, variants: { ...lay.variants, [combo]: { ...variant, ...patch } } });
    const onName = (key, value) => commitLayout({ ...lay, labels: { ...lay.labels, [key]: value } });
    const onAccent = (key, value) => commitLayout({ ...lay, accents: { ...(lay.accents || {}), [key]: value } });
    const onView = (key, v) => setViewSel((p) => ({ ...p, [key]: v }));
    const onWidth = (key, delta) => { const cur = (variant.colW || {})[key] || 1; const next = Math.max(0.5, Math.min(4, Math.round((cur + delta) * 4) / 4)); onVariant({ colW: { ...(variant.colW || {}), [key]: next } }); };

    // ---- per-slot mutators ----
    const clearFixed = (key) => { const d = clone(draft); ensureUnits(d, defs); writeEV(d, key, { ...EMPTY }); setDraft(d); };
    const setFixedPos = (key, v) => { const d = clone(draft); ensureUnits(d, defs); const cur = getEV(d, key); writeEV(d, key, { ...cur, pos: v }); setDraft(d); };
    const clearUnit = (uk, idx) => { const d = clone(draft); ensureUnits(d, defs); if (d.special_units[uk]) d.special_units[uk][idx] = { ...EMPTY }; setDraft(d); };
    const setUnitPos = (uk, idx, v) => { const d = clone(draft); ensureUnits(d, defs); if (d.special_units[uk] && d.special_units[uk][idx]) d.special_units[uk][idx] = { ...d.special_units[uk][idx], pos: v }; setDraft(d); };
    const listArr = (d, field) => field === "shootout" ? (d.shootout_order = d.shootout_order || []) : field === "reserves" ? (d.reserves = d.reserves || []) : ((d.special_units = d.special_units || {}), (d.special_units[field] = d.special_units[field] || []));
    const clearList = (field, idx) => { const d = clone(draft); listArr(d, field); if (field === "shootout") d.shootout_order = d.shootout_order.filter((_, i) => i !== idx); else if (field === "reserves") d.reserves = d.reserves.filter((_, i) => i !== idx); else d.special_units[field] = (d.special_units[field] || []).filter((_, i) => i !== idx); setDraft(d); };
    const setListPos = (field, idx, v) => { const d = clone(draft); listArr(d, field); const arr = field === "shootout" ? d.shootout_order : field === "reserves" ? d.reserves : d.special_units[field]; if (arr[idx]) arr[idx] = { ...arr[idx], pos: v, position: v }; setDraft(d); };

    const mut = { primary, clearFixed, setFixedPos, clearUnit, setUnitPos, clearList, setListPos };

    const onDragEnd = ({ source, destination, draggableId }) => {
        unlockScroll();
        if (!destination) return;
        const s = parseId(draggableId);
        let pid;
        if (s.kind === "pool") pid = s.pid;
        else if (s.kind === "res") pid = (draft.reserves[s.idx] || {}).player_id;
        else if (s.kind === "so") pid = (draft.shootout_order?.[s.idx] || {}).player_id;
        else if (["ea", "ir", "ltir", "g3"].includes(s.kind)) pid = ((draft.special_units?.[s.kind] || [])[s.idx] || {}).player_id;
        else if (s.kind === "ev") pid = (getEV(draft, s.key) || {}).player_id;
        else if (s.kind === "su") pid = ((draft.special_units?.[s.unit] || [])[s.idx] || {}).player_id;
        if (!pid) return;
        const p = byId[pid] || { name: pid };
        const val = { player_id: pid, player_name: p.name || "" };
        const d = clone(draft); ensureUnits(d, defs);
        const dst = destination.droppableId;
        if (dst === source.droppableId && dst !== "shootout" && dst !== "ea" && dst !== "ir" && dst !== "ltir" && dst !== "g3" && dst !== "reserves") return;

        if (dst === "pool") { clearSource(d, s); return setDraft(d); }
        if (dst === "shootout") {
            if (s.kind === "so") { const arr = [...d.shootout_order]; const [m] = arr.splice(s.idx, 1); arr.splice(destination.index, 0, m); d.shootout_order = arr; return setDraft(d); }
            if (!(d.shootout_order || []).some((x) => x.player_id === pid)) d.shootout_order = [...(d.shootout_order || []), val];
            return setDraft(d);
        }
        if (dst === "reserves") {
            if (s.kind === "res") { const arr = [...d.reserves]; const [m] = arr.splice(s.idx, 1); arr.splice(destination.index, 0, m); d.reserves = arr; return setDraft(d); }
            clearSource(d, s); removeFromExclusive(d, pid, defs);
            d.reserves = [...(d.reserves || []), { label: "Scratch", position: posTag(p), player_id: pid, player_name: p.name || "" }];
            return setDraft(d);
        }
        if (dst === "ea" || dst === "ir" || dst === "ltir" || dst === "g3") {
            const fld = dst; d.special_units = d.special_units || {}; d.special_units[fld] = d.special_units[fld] || [];
            if (s.kind === fld) { const arr = [...d.special_units[fld]]; const [m] = arr.splice(s.idx, 1); arr.splice(destination.index, 0, m); d.special_units[fld] = arr; return setDraft(d); }
            clearSource(d, s); if (fld !== "ea") removeFromExclusive(d, pid, defs);
            if (!d.special_units[fld].some((x) => x?.player_id === pid)) d.special_units[fld] = [...d.special_units[fld], val];
            return setDraft(d);
        }
        if (dst.startsWith("su:")) {
            const [, unit, idxS] = dst.split(":"); const idx = +idxS; const box = boxForUnitKey(defs, unit);
            if (box && box.dup) {
                const prev = d.special_units[unit][idx] || { ...EMPTY };
                d.special_units[unit] = d.special_units[unit].map((x) => (x && x.player_id === pid ? { ...EMPTY } : x));
                d.special_units[unit][idx] = val;
                if (s.kind === "su" && s.unit === unit) d.special_units[unit][s.idx] = prev;
                return setDraft(d);
            }
            clearSource(d, s); removeFromExclusive(d, pid, defs);
            d.special_units[unit][idx] = val;
            return setDraft(d);
        }
        if (dst.startsWith("slot:")) {
            const key = dst.slice(5);
            const prev = { ...getEV(d, key) };
            clearSource(d, s); removeFromExclusive(d, pid, defs);
            writeEV(d, key, val);
            if (s.kind === "ev" && s.key !== key && prev.player_id) writeEV(d, s.key, { player_id: prev.player_id, player_name: prev.player_name });
            return setDraft(d);
        }
    };

    const columns = useMemo(() => buildColumns(draft, "special", lay, viewSel, true, mut), [draft, lay, viewSel]); // eslint-disable-line

    return (
        <DragDropContext onDragStart={lockScroll} onDragEnd={onDragEnd} autoScrollerOptions={{ disabled: true }}>
            <div className="space-y-2.5" data-testid="team-lineup-card-edit">
                <ToolbarControls variant={variant} editDevice={editDevice} setEditDevice={setEditDevice} onVariant={onVariant} />
                <p className="text-[11px] text-[#64748B]">Drag players from the pool into any slot. Rename categories/lines inline, set each player's position tag, resize columns (− / +), and size icons/text in px. Every category is always shown; empty slots stay visible for the locked design.</p>
                <div className="mx-auto w-full transition-all" style={{ maxWidth: CANVAS_MAX[editDevice] }} data-testid={`lineup-canvas-${editDevice}`}>
                    {editDevice !== "pc" && <div className="text-center text-[9px] uppercase tracking-widest text-white/30 font-bold mb-1.5">{editDevice} preview · {CANVAS_MAX[editDevice]}</div>}
                    <LineupBoard columns={columns} editable byId={byId} lay={elay} sz={sz} tagStyle={tagStyle} colW={variant.colW} onName={onName} onAccent={onAccent} onView={onView} onWidth={onWidth} />
                </div>

                <div className="rosterly-card p-3" data-testid="lineup-edit-pool">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-[#E9D5FF]">Available <span className="text-[#64748B] ml-1">({available.length})</span></h4>
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-[#0A0B10] border border-[#1E2233] rounded-md px-2 py-1 text-xs text-white w-[160px] focus:outline-none focus:border-[#9D4CDD]" data-testid="lineup-edit-pool-search" />
                    </div>
                    <Droppable droppableId="pool" direction="horizontal" type="PLAYER">
                        {(prov, snap) => (
                            <div ref={prov.innerRef} {...prov.droppableProps} className={`min-h-[72px] rounded-md p-2 flex flex-wrap gap-2.5 content-start transition-colors ${snap.isDraggingOver ? "bg-[#9D4CDD]/10 ring-1 ring-[#9D4CDD]" : "bg-black/20"}`}>
                                {available.length === 0 && !snap.isDraggingOver
                                    ? <p className="text-xs text-[#64748B] italic py-4 w-full text-center">All players placed — drag one back here to free it.</p>
                                    : available.map((p, i) => (
                                        <Draggable key={p.id} draggableId={`pool:${p.id}`} index={i}>
                                            {(prov2, s2) => <div ref={prov2.innerRef} {...prov2.draggableProps}><EditChip dragHandleProps={prov2.dragHandleProps} name={p.name} photo={p.photo_url} meta={p} slot={p} posDefault={posTag(p)} accent={primary} tagStyle={tagStyle} dragging={s2.isDragging} lay={elay} sz={sz} /></div>}
                                        </Draggable>
                                    ))}
                                <div style={{ display: "none" }}>{prov.placeholder}</div>
                            </div>
                        )}
                    </Droppable>
                </div>
            </div>
        </DragDropContext>
    );
}

// ============================================================================
//  PUBLIC CARD
// ============================================================================
export const LineupCard = ({ lineup, primary, playerMap, title = "Projected Lineup", editor = null }) => {
    const qc = useQueryClient();
    const { data: rawLayout } = useQuery({ queryKey: ["lineup-layout"], queryFn: () => api.get("/lineup-layout").then((r) => r.data), staleTime: 60000 });
    const lay = useMemo(() => layoutOf(rawLayout), [rawLayout]);
    const [viewSel, setViewSel] = useState({});
    const [userView, setUserView] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem("rosterly_lineup_view") : null));
    const setView = (v) => { setUserView(v); try { localStorage.setItem("rosterly_lineup_view", v); } catch {} api.put("/account", { lineup_view: v }).catch(() => {}); };
    const { data: lcAccount } = useQuery({ queryKey: ["account"], queryFn: () => api.get("/account").then((r) => r.data), retry: false, staleTime: 60000 });
    useEffect(() => {
        const lv = lcAccount?.profile?.lineup_view;
        if (lv && lv !== userView) { setUserView(lv); try { localStorage.setItem("rosterly_lineup_view", lv); } catch {} }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lcAccount?.profile?.lineup_view]);
    const layoutMut = useMutation({ mutationFn: (next) => api.put("/admin/lineup-layout", { layout: next }).then((r) => r.data) });
    const commitLayout = (next) => { qc.setQueryData(["lineup-layout"], next); layoutMut.mutate(next); };

    const device = usePublicDevice();
    const narrow = useNarrow();
    const variant = useMemo(() => getVariant(lay, "special", "pc"), [lay]);
    const elay = useMemo(() => effLay(lay, variant), [lay, variant]);
    const sz = useMemo(() => buildSz(variant.iconPx, variant.textPx), [variant.iconPx, variant.textPx]);
    const tagStyle = variant.tagStyle || "accent";

    if (editor) {
        return (
            <div data-testid="team-lineup-card" className="space-y-4">
                {title && <SectionHeading title={title} />}
                <EditableLineup draft={lineup} setDraft={editor.setDraft} pool={editor.pool} players={editor.players} primary={primary} lay={lay} commitLayout={commitLayout} />
            </div>
        );
    }
    if (!lineup) return null;

    // Per-user roster view toggle (saved in localStorage). "full" = every column
    // (stretches wide); "simple" = just Lines + Pairs + Goalies, centred, so the
    // board isn't overwhelming. Narrow devices (mobile / vertical tablet) ALWAYS
    // show the full team stacked by section — the toggle is hidden there.
    const view = narrow ? "full" : (userView || lay.publicView || "full");
    let columns = buildColumns(lineup, "special", lay, viewSel, false, { primary });
    if (view === "simple") columns = columns.filter((c) => c.key === "lines" || c.key === "pairs");
    const centeredSimple = view === "simple" && !narrow;

    return (
        <div data-testid="team-lineup-card" className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                {title ? <SectionHeading title={title} /> : <span />}
                {!narrow && (
                    <div className="inline-flex items-center rounded-lg border border-white/10 bg-black/25 p-0.5" data-testid="lineup-view-toggle">
                        {[["full", "Full Team"], ["simple", "Simple"]].map(([v, l]) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setView(v)}
                                data-testid={`lineup-view-${v}`}
                                className={`px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === v ? "bg-[#9D4CDD] text-white" : "text-[#94A3B8] hover:text-white"}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className={centeredSimple ? "mx-auto w-full" : "w-full"} style={centeredSimple ? { maxWidth: 760 } : undefined}>
                <LineupBoard columns={columns} editable={false} playerMap={playerMap} lay={elay} sz={sz} tagStyle={tagStyle} colW={variant.colW} onView={(k, v) => setViewSel((p) => ({ ...p, [k]: v }))} onName={() => {}} onAccent={() => {}} />
            </div>
            {lineup.updated_at && <p className="text-[10px] text-[#64748B] font-mono-stat mt-1">Updated {new Date(lineup.updated_at).toLocaleDateString()}</p>}
        </div>
    );
};

export default LineupCard;
